import * as React from 'react';

import pluralize from 'pluralize';

import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import AdminRenderUploadErrorsBanner from 'admin/rostering/components/AdminRenderUploadErrorsBanner';
import AdminSisSyncStatusLabel from 'admin/rostering/components/AdminSisSyncStatusLabel';
import Button from 'components/Button';
import Link from 'components/Link';
import TitleHeader from 'components/TitleHeader';

import {
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
  AdminDistrictSisDashboard_organizations_sisSyncOverview as SisSyncOverview,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus_schoolMappings as SchoolMapping,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  title: string;
  subtitle: React.ReactNode;
  childOrganizations: ChildOrganization[];
  sisSyncOverview: SisSyncOverview | null;
  orgUuid: string;
  canOnlySeeReviewScreen: boolean;
  canOnlySeeRosteringStagingArea: boolean;
};

export default function AdminDistrictSisDashboardSftpHeader(props: Props) {
  function numSchoolsToMap(schoolMappings: SchoolMapping[], childOrgs: ChildOrganization[]) {
    const numPaidChildOrgs = childOrgs.filter(org => org?.paidAt).length;
    const numMappedChildOrgs = schoolMappings.filter(mapping => mapping?.remindSchoolUuid).length;

    return numPaidChildOrgs - numMappedChildOrgs;
  }

  function renderMatchingBanner(orgUuid: string, displayNum: number) {
    const button = (
      <Button
        eventElementName="AdminDistrictSisDashboardSftpHeader-matchYourSchools"
        href={`/sftp_sync_setup/matching?district_uuid=${orgUuid}`}>
        Match your schools
      </Button>
    );

    // We might find more schools in spreadsheets than we can map. (ex. not all schools are paid)
    // We might also find that some schools are already mapped.
    // So just take the smallest number and show that.

    return (
      <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={button}>
        {`We’ve detected ${displayNum} ${pluralize(
          'school',
          displayNum
        )} that must be matched before the initial sync can begin for these schools.`}
      </AdminNoticeBanner>
    );
  }

  function renderSetupBanner(orgUuid: string) {
    return (
      <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={null}>
        Get started by entering the SFTP details into your SIS or FTP client. View them in the upper
        right or{' '}
        <Link href={`/sftp_sync_setup/details?district_uuid=${orgUuid}`} weight="semibold">
          click here
        </Link>
        .
      </AdminNoticeBanner>
    );
  }

  function renderAwaitingFilesBanner() {
    return (
      <AdminNoticeBanner glyph="clock" color="sunrise" actionElement={null}>
        We’re processing your files now. Please check back in 3 minutes.
      </AdminNoticeBanner>
    );
  }

  function renderAwaitingReviewBanner() {
    return (
      <AdminNoticeBanner glyph="clock" color="sunrise" actionElement={null}>
        Our implementation team is reviewing your data. You'll receive an email with next steps in
        two business days.
      </AdminNoticeBanner>
    );
  }

  function renderStagingEnvironmentBanner() {
    return (
      <AdminNoticeBanner glyph="clock" color="koala" actionElement={null}>
        Review each school's sync to fix errors. When you're ready to push data live, contact our
        implementation team.
      </AdminNoticeBanner>
    );
  }

  const {
    title,
    subtitle,
    sisSyncOverview,
    childOrganizations,
    orgUuid,
    canOnlySeeReviewScreen,
    canOnlySeeRosteringStagingArea,
  } = props;

  const schoolMappings = sisSyncOverview?.districtStatus?.schoolMappings ?? [];
  const awaitingFiles = sisSyncOverview?.districtStatus?.awaitingFiles ?? false;
  const filesZipUrl = sisSyncOverview?.districtStatus?.filesZipUrl;
  const uploadErrors = sisSyncOverview?.districtStatus?.uploadErrors ?? [];
  const numSchoolsToBeMapped = numSchoolsToMap(schoolMappings, childOrganizations);

  const noSchoolsMapped = !schoolMappings || schoolMappings.length === 0;
  const someSftpSchoolsNotMapped = numSchoolsToBeMapped > 0;
  const isInactive =
    (uploadErrors && uploadErrors.length !== 0) ||
    awaitingFiles ||
    noSchoolsMapped ||
    someSftpSchoolsNotMapped;

  function renderBanner(): React.ReactNode {
    if (uploadErrors && uploadErrors.length !== 0) {
      return (
        <AdminRenderUploadErrorsBanner
          uploadErrors={uploadErrors}
          filesZipUrl={filesZipUrl ?? undefined}
        />
      );
    } else if (awaitingFiles) {
      return renderAwaitingFilesBanner();
    } else if (noSchoolsMapped) {
      return renderSetupBanner(orgUuid);
    } else if (someSftpSchoolsNotMapped) {
      return renderMatchingBanner(orgUuid, numSchoolsToBeMapped);
    } else if (canOnlySeeReviewScreen) {
      return renderAwaitingReviewBanner();
    } else if (canOnlySeeRosteringStagingArea) {
      return renderStagingEnvironmentBanner();
    } else {
      return null;
    }
  }

  function renderTitle() {
    if (canOnlySeeReviewScreen || canOnlySeeRosteringStagingArea) {
      if (isInactive) {
        return <AdminSisSyncStatusLabel status="inactive" />;
      } else {
        return <AdminSisSyncStatusLabel status="pending" />;
      }
    }

    return title;
  }

  return (
    <TitleHeader
      title={renderTitle()}
      subtext={subtitle}
      action={
        <Button level="secondary" href={`/sftp_sync_setup/details?district_uuid=${orgUuid}`}>
          SIS sync setup
        </Button>
      }
      actionPlacement="right"
      className="AdminDistrictSisDashboardSftpHeader">
      {renderBanner()}
    </TitleHeader>
  );
}
